import React from "react";
import { Breadcrumb, Button, Card, Form, Table, Badge } from "components/ui/BootstrapCompat";
import { useQuery, useQueryClient } from "react-query";
import useLocalStorage from "helpers/useLocalStorage";
import { userGetMany, userDeleteOne } from "services/user.service";
import { successAdd, errorAdd } from "helpers/sweetalert";
import Swal from "sweetalert2";
import PopUpCreateUser from "components/popup/PopUpCreateUser";
import PopUpEditUser from "components/popup/PopUpEditUser";

type Props = {};

export default function UserPage({}: Props) {
  const [popup, setPopup] = React.useState<any>({});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const queryClient = useQueryClient();

  const { data, isLoading, isRefetching, refetch } = useQuery(
    ["user-list"],
    async () => {
      const response = await userGetMany({ findby: {}, token, businessId, limit: 100 });
      return response;
    }
  );

  const filteredData = data?.filter((user: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user?.name?.toLowerCase().includes(search) ||
      user?.email?.toLowerCase().includes(search) ||
      user?.phone?.includes(search) ||
      user?._id?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil((filteredData?.length || 0) / pageSize);
  const paginatedData = filteredData?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "ຢືນຢັນການລຶບ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ລຶບ",
      cancelButtonText: "ຍົກເລີກ",
    });
    if (!result.isConfirmed) return;
    try {
      await userDeleteOne({ id, token, businessId });
      await queryClient.invalidateQueries(["user-list"]);
      successAdd("ລຶບຜູ້ໃຊ້ສຳເລັດ");
    } catch (error: any) {
      errorAdd(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ເຄື່ອງມືພື້ນຖານ</Breadcrumb.Item>
        <Breadcrumb.Item active>ຜູ້ໃຊ້</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <Card.Header>ຜູ້ໃຊ້ທັງໝົດ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.length || 0}
                <span className="text-[14px]">ຄົນ</span>
              </h1>
              <div className="text-gray-600 text-[12px]">
                ກຳລັງໃຊ້ງານ{" "}
                <span className="text-primary">
                  {data?.filter((u: any) => u.status === "active").length || 0}
                </span>{" "}
                ຄົນ
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຜູ້ດູແລລະບົບ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.filter((u: any) => u.role === "admin").length || 0}
                <span className="text-[14px]">ຄົນ</span>
              </h1>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຜູ້ໃຊ້ທົ່ວໄປ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.filter((u: any) => u.role === "user").length || 0}
                <span className="text-[14px]">ຄົນ</span>
              </h1>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຜູ້ໃຊ້ບໍ່ເຄື່ອນໄຫວ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.filter((u: any) => u.status === "inactive").length || 0}
                <span className="text-[14px]">ຄົນ</span>
              </h1>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="mb-2 flex flex-wrap gap-2 rounded bg-white p-2">
        <Form.Control
          type="text"
          placeholder="ຄົ້ນຫາ..."
          className="min-w-0 flex-[1_1_100%] border border-primary md:flex-none md:w-[200px]"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <Button variant="primary" className="flex flex-1 items-center sm:flex-none">
          <span className="material-icons">search</span>
        </Button>
        <Button
          variant="outline-primary"
          className="flex-1 sm:flex-none"
          onClick={() => refetch()}
        >
          Refetch
        </Button>
        <div className="hidden flex-1 md:block" />
        <Button
          variant="outline-primary"
          className="flex-[1_1_100%] sm:flex-none"
          onClick={() => setPopup({ PopUpCreateUser: true })}
        >
          ເພີ່ມຜູ້ໃຊ້
        </Button>
      </div>

      <div>
        <Card border="primary">
          <Card.Header className="p-1">ລາຍການຜູ້ໃຊ້</Card.Header>
          <Table striped bordered hover size="sm" className="min-w-[600px]">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left border-b w-[60px]">ລ/ດ</th>
                <th className="py-2 px-3 text-left border-b">ຊື່ຜູ້ໃຊ້</th>
                <th className="py-2 px-3 text-left border-b">ອີເມວ</th>
                <th className="py-2 px-3 text-left border-b">ເບີໂທ</th>
                <th className="py-2 px-3 text-center border-b">ສິດ</th>
                <th className="py-2 px-3 text-center border-b">ສະຖານະ</th>
                <th className="py-2 px-3 text-right border-b">ເຄື່ອງມື</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isRefetching ? (
                <tr>
                  <td colSpan={7} className="py-4 text-center">
                    <div className="flex justify-center items-center">
                      <span>ກຳລັງໂຫລດຂໍ້ມູນ...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedData && paginatedData.length > 0 ? (
                paginatedData.map((user: any, i: number) => (
                  <tr key={user._id}>
                    <td className="py-2 px-3 border-b">{(currentPage - 1) * pageSize + i + 1}</td>
                    <td className="py-2 px-3 border-b">
                      <div className="flex flex-col gap-1">
                        <div className="font-bold text-[16px] text-primary">
                          {user?.name || "--"}
                        </div>
                        <div className="text-[10px] text-neutral-500">
                          ID: {user?._id}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 border-b">
                      {user?.email || "--"}
                    </td>
                    <td className="py-2 px-3 border-b">
                      {user?.phone || "--"}
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      <Badge variant={user?.role === "admin" ? "danger" : "secondary"}>
                        {user?.role === "admin" ? "ຜູ້ດູແລ" : "ຜູ້ໃຊ້"}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      <Badge variant={user?.status === "active" ? "success" : "secondary"}>
                        {user?.status === "active" ? "ເຄື່ອນໄຫວ" : "ບໍ່ເຄື່ອນໄຫວ"}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 border-b">
                      <div className="flex justify-end gap-1">
                        <button
                          className="btn btn-outline-primary btn-sm flex items-center gap-1"
                          onClick={() => setPopup({ PopUpEditUser: true, selectedUser: user })}
                        >
                          ແກ້ໄຂ
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm flex items-center gap-1"
                          onClick={() => handleDelete(user._id)}
                        >
                          ລຶບ
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-4 text-center">
                    ບໍ່ພົບຂໍ້ມູນ
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      </div>

      {totalPages > 1 && (
        <div className="mt-2 flex flex-col gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-500">
            ສະແດງ {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredData?.length || 0)} ຈາກ {filteredData?.length || 0} ລາຍການ
          </div>
          <div className="flex max-w-full gap-1 overflow-x-auto pb-1 sm:pb-0">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              «
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ‹
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`ellipsis-${idx}`} className="px-2 flex items-center text-gray-400">…</span>
                ) : (
                  <Button
                    key={p}
                    variant={currentPage === p ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => setCurrentPage(p as number)}
                  >
                    {p}
                  </Button>
                )
              )}
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              ›
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      <PopUpCreateUser
        open={!!popup.PopUpCreateUser}
        onClose={() => setPopup({})}
        businessId={businessId}
      />

      <PopUpEditUser
        open={!!popup.PopUpEditUser}
        onClose={() => setPopup({})}
        data={popup.selectedUser}
        businessId={businessId}
      />
    </div>
  );
}
